import React, { useEffect, useRef, useState, useCallback } from "react";
import { X } from "lucide-react";
import ModalMotionShell from "./ModalMotionShell";
import ModalDragHandle from "./ModalDragHandle";
import { apiUrl } from "../utils/apiBase";

function useDraggable() {
  const ref = useRef(null);
  const [pos, setPos] = useState({ x: 0, y: 0 });

  const onMouseDown = (e) => {
    if (e.button !== 0) return;

    const startX = e.clientX - pos.x;
    const startY = e.clientY - pos.y;

    const onMove = (ev) => {
      setPos({
        x: ev.clientX - startX,
        y: ev.clientY - startY,
      });
    };

    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  return { ref, pos, onMouseDown, setPos };
}

export default function HelpSupportModal({
  open,
  onClose,
  language = "de",
  initialSection = "overview",
  initialCategory = "",
}) {
  const [helpActiveSection, setHelpActiveSection] = useState("overview");
  const [supportForm, setSupportForm] = useState({
    email: "",
    category: "",
    description: "",
    urgent: false,
    walletAddress: "",
    consent: false,
    calculationErrorConsent: false,
  });
  const [supportAttachments, setSupportAttachments] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isSubmittingSupport, setIsSubmittingSupport] = useState(false);
  const [supportSubmitted, setSupportSubmitted] = useState(false);
  const [isHandheldDevice, setIsHandheldDevice] = useState(false);

  const dragHelp = useDraggable();
  const helpContentRef = useRef(null);

  const closeModal = useCallback(() => {
    if (window._supportModalCloseHandler) {
      window._supportModalCloseHandler();
    } else {
      onClose?.();
    }
  }, [onClose]);

  const getHelpTabButtonClasses = useCallback(
    (section) => {
      const isActive = helpActiveSection === section;
      const baseClasses = isHandheldDevice
        ? "flex flex-col items-center justify-center gap-2 flex-shrink-0 min-w-[160px] px-4 py-3 rounded-2xl"
        : "flex items-center gap-3 w-full text-left px-4 py-3 rounded-lg";
      const stateClasses = isActive
        ? isHandheldDevice
          ? "bg-blue-50 text-blue-800 border border-blue-200 shadow-sm"
          : "bg-blue-50 text-blue-800 border border-blue-200 shadow-sm"
        : isHandheldDevice
        ? "bg-white text-gray-700 border border-gray-200 shadow-sm"
        : "text-gray-700 hover:bg-gray-50 hover:shadow-sm border border-transparent";

      return `${baseClasses} transition-all duration-200 ${stateClasses}`;
    },
    [helpActiveSection, isHandheldDevice]
  );

  const getHelpTabSubtitleClasses = useCallback(
    (section) => {
      const isActive = helpActiveSection === section;
      const base = isHandheldDevice ? "text-xs text-center" : "text-xs";
      return `${base} ${isActive ? "text-blue-700" : "text-gray-500"}`;
    },
    [helpActiveSection, isHandheldDevice]
  );

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        closeModal();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, closeModal]);

  useEffect(() => {
    if (!open) return;

    const mediaQuery = window.matchMedia?.("(pointer: coarse)");

    const compute = () => {
      const isSmall = window.innerWidth < 768;
      setIsHandheldDevice(Boolean(mediaQuery?.matches && isSmall));
    };

    compute();

    if (mediaQuery?.addEventListener) {
      mediaQuery.addEventListener("change", compute);
    } else if (mediaQuery?.addListener) {
      mediaQuery.addListener(compute);
    }

    window.addEventListener("resize", compute);

    return () => {
      if (mediaQuery?.removeEventListener) {
        mediaQuery.removeEventListener("change", compute);
      } else if (mediaQuery?.removeListener) {
        mediaQuery.removeListener(compute);
      }
      window.removeEventListener("resize", compute);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;

    setHelpActiveSection(initialCategory ? "support" : (initialSection || "overview"));
    setSupportSubmitted(false);
    setSupportAttachments([]);
    setSupportForm((prev) => ({
      ...prev,
      category: initialCategory || "",
    }));
  }, [open, initialSection, initialCategory]);

  useEffect(() => {
    if (open && helpContentRef.current) {
      helpContentRef.current.scrollTop = 0;
    }
  }, [helpActiveSection, open]);

  useEffect(() => {
    if (open) {
      dragHelp.setPos({ x: 0, y: 0 });
    }
  }, [open, dragHelp.setPos]);

  const handleSupportSubmit = async (e) => {
    e.preventDefault();
    setIsSubmittingSupport(true);

    try {
      const formData = new FormData();
      formData.append("email", supportForm.email);
      formData.append("category", supportForm.category);
      formData.append("description", supportForm.description);
      formData.append("urgent", supportForm.urgent ? "true" : "false");
      formData.append("walletAddress", supportForm.walletAddress || "");
      formData.append("consent", supportForm.consent ? "true" : "false");
      formData.append(
        "calculationErrorConsent",
        supportForm.calculationErrorConsent ? "true" : "false"
      );

      supportAttachments.forEach((file) => {
        formData.append("attachments", file);
      });

      const response = await fetch(apiUrl("/api/send-support-email"), {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || "Support request failed");
      }

      setSupportSubmitted(true);
      setIsSubmittingSupport(false);
    } catch (error) {
            setIsSubmittingSupport(false);
    }
  };

  return (
    <ModalMotionShell
      isOpen={open}
      onClose={closeModal}
      zIndexClass="z-[300]"
      stretch={isHandheldDevice}
      backdropClassName={!isHandheldDevice ? "!px-2 !py-2" : ""}
    >
      <div
        ref={dragHelp.ref}
        className={`bg-white w-full overflow-hidden ${
          isHandheldDevice
            ? "rounded-none shadow-none flex h-full min-h-0 flex-col bg-white"
            : `rounded-2xl shadow-2xl max-w-5xl ${
                helpActiveSection === "support"
                  ? "h-[98vh] max-h-[98vh]"
                  : "max-h-[90vh]"
              } overflow-hidden flex`
        }`}
        style={
          isHandheldDevice
            ? {
                position: "fixed",
                inset: 0,
                width: "100vw",
                height: "100dvh",
                maxHeight: "100dvh",
                zIndex: 30001,
                paddingTop: "max(env(safe-area-inset-top), 12px)",
                paddingBottom: "max(env(safe-area-inset-bottom), 12px)",
              }
            : {
                transform: `translate(${dragHelp.pos.x}px, ${dragHelp.pos.y}px)`,
                zIndex: 30001,
              }
        }
        onMouseDown={(e) => e.stopPropagation()}
        onDragStart={(e) => e.preventDefault()}
      >
        <div
          className={
            isHandheldDevice
              ? "w-full bg-white border-b border-gray-200"
              : "w-80 bg-white border-r border-gray-200"
          }
        >
          <div className="h-[5px] shrink-0 bg-gradient-to-r from-blue-700 via-blue-600 to-indigo-600" />

          <div
            className={`modal-header ${
              isHandheldDevice
                ? "p-4 border-b border-gray-200"
                : "drag-handle p-6 border-b border-gray-200 cursor-move select-none"
            }`}
            onMouseDown={(e) => {
              if (!isHandheldDevice && e.button === 0) {
                e.preventDefault();
                e.stopPropagation();
                dragHelp.onMouseDown(e);
              }
            }}
          >
            <div className={`flex items-center gap-3 ${isHandheldDevice ? "justify-center" : ""}`}>
              {!isHandheldDevice && (
                <ModalDragHandle
                  onMouseDown={dragHelp.onMouseDown}
                  title={language === "en" ? "Move" : "Verschieben"}
                  ariaLabel={language === "en" ? "Move modal" : "Modal verschieben"}
                  className="mt-0.5"
                />
              )}
              <h2
                className={`text-xl font-bold text-gray-900 ${
                  isHandheldDevice ? "text-center" : ""
                }`}
              >
                {language === "en" ? "Help & Support" : "Hilfe & Support"}
              </h2>
            </div>
          </div>

          <nav
            className={
              isHandheldDevice
                ? "flex gap-3 overflow-x-auto px-4 py-3"
                : "p-4 space-y-2"
            }
            style={isHandheldDevice ? { WebkitOverflowScrolling: "touch" } : undefined}
          >
            <button
              type="button"
              onClick={() => setHelpActiveSection("overview")}
              className={getHelpTabButtonClasses("overview")}
            >
              <span className="text-xl">📋</span>
              <div className={`flex flex-col gap-1 ${isHandheldDevice ? "items-center text-center" : ""}`}>
                <div className={`font-medium ${isHandheldDevice ? "text-center" : ""}`}>
                  {language === "en" ? "Overview" : "Übersicht"}
                </div>
                <div className={getHelpTabSubtitleClasses("overview")}>
                  {language === "en" ? "Getting started" : "Erste Schritte"}
                </div>
              </div>
            </button>

            <button
              type="button"
              onClick={() => setHelpActiveSection("tutorial")}
              className={getHelpTabButtonClasses("tutorial")}
            >
              <span className="text-xl">🎓</span>
              <div className={`flex flex-col gap-1 ${isHandheldDevice ? "items-center text-center" : ""}`}>
                <div className={`font-medium ${isHandheldDevice ? "text-center" : ""}`}>
                  {language === "en" ? "Tutorial" : "Anleitung"}
                </div>
                <div className={getHelpTabSubtitleClasses("tutorial")}>
                  {language === "en" ? "How to use V3Tax" : "V3Tax Bedienung"}
                </div>
              </div>
            </button>

            <button
              type="button"
              onClick={() => setHelpActiveSection("faq")}
              className={getHelpTabButtonClasses("faq")}
            >
              <span className="text-xl">❓</span>
              <div className={`flex flex-col gap-1 ${isHandheldDevice ? "items-center text-center" : ""}`}>
                <div className={`font-medium ${isHandheldDevice ? "text-center" : ""}`}>
                  {language === "en" ? "FAQ" : "Häufige Fragen"}
                </div>
                <div className={getHelpTabSubtitleClasses("faq")}>
                  {language === "en" ? "Common questions" : "Oft gestellte Fragen"}
                </div>
              </div>
            </button>

            <button
              type="button"
              onClick={() => setHelpActiveSection("support")}
              className={getHelpTabButtonClasses("support")}
            >
              <span className="text-xl">🎫</span>
              <div className={`flex flex-col gap-1 ${isHandheldDevice ? "items-center text-center" : ""}`}>
                <div className={`font-medium ${isHandheldDevice ? "text-center" : ""}`}>
                  {language === "en" ? "Support Ticket" : "Support Anfrage"}
                </div>
                <div className={getHelpTabSubtitleClasses("support")}>
                  {language === "en" ? "Get personal help" : "Persönliche Hilfe"}
                </div>
              </div>
            </button>
          </nav>
        </div>

        <div className={`flex-1 min-h-0 flex flex-col ${isHandheldDevice ? "overflow-hidden bg-white" : ""}`}>
          <div
            className={`shrink-0 border-b border-gray-200 flex items-center justify-between bg-white ${
              isHandheldDevice ? "px-4 py-3" : "px-6 py-4"
            }`}
          >
            <h3 className="text-lg font-semibold text-gray-900">
              {helpActiveSection === "overview" &&
                (language === "en" ? "Getting Started with V3Tax" : "Erste Schritte mit V3Tax")}
              {helpActiveSection === "tutorial" &&
                (language === "en" ? "How to Use V3Tax" : "V3Tax Bedienungsanleitung")}
              {helpActiveSection === "faq" &&
                (language === "en" ? "Frequently Asked Questions" : "Häufig gestellte Fragen")}
              {helpActiveSection === "support" &&
                (language === "en" ? "Create Support Ticket" : "Support Anfrage erstellen")}
            </h3>

            <button
              onClick={closeModal}
              className="group text-gray-400 hover:text-gray-600 transition p-2 -m-2 flex-shrink-0"
              aria-label={language === "en" ? "Close" : "Schließen"}
              type="button"
            >
              <X className="w-5 h-5 transition-transform duration-200 group-hover:rotate-90" />
            </button>
          </div>

          <div
            ref={helpContentRef}
            className={`flex-1 min-h-0 overflow-y-auto overscroll-contain ${
              isHandheldDevice ? "p-4 pb-6" : "p-6"
            }`}
            style={isHandheldDevice ? { WebkitOverflowScrolling: "touch" } : undefined}
          >
            {helpActiveSection === "overview" && (
              <div className="space-y-6">
                <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                  <div className="text-sm font-semibold text-gray-900">
                    {language === "en" ? "Welcome to V3Tax" : "Willkommen bei V3Tax"}
                  </div>
                  <div className="mt-1 text-sm text-gray-600 leading-relaxed">
                    {language === "en"
                      ? "V3Tax is your comprehensive blockchain analysis tool for preparing cryptocurrency tax returns. Our platform automatically processes your VeChain transactions and generates detailed tax reports."
                      : "V3Tax ist Ihr umfassendes Blockchain-Analyse-Tool zur Vorbereitung von Kryptowährungs-Steuererklärungen. Unsere Plattform verarbeitet automatisch Ihre VeChain-Transaktionen und erstellt detaillierte Steuerberichte."}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                    <h5 className="text-sm font-semibold text-gray-900 mb-2">
                      ✅ {language === "en" ? "What V3Tax does" : "Was V3Tax macht"}
                    </h5>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• {language === "en" ? "Analyzes VET & VTHO transactions" : "Analysiert VET & VTHO Transaktionen"}</li>
                      <li>• {language === "en" ? "Calculates FIFO gains/losses" : "Berechnet FIFO Gewinne/Verluste"}</li>
                      <li>• {language === "en" ? "Tracks NFT trades & mints" : "Verfolgt NFT Trades & Mints"}</li>
                      <li>• {language === "en" ? "Generates PDF & CSV reports" : "Erstellt PDF & CSV Berichte"}</li>
                    </ul>
                  </div>

                  <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                    <h5 className="text-sm font-semibold text-gray-900 mb-2">
                      ⚠️ {language === "en" ? "Important Notes" : "Wichtige Hinweise"}
                    </h5>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• {language === "en" ? "Not professional tax advice" : "Keine professionelle Steuerberatung"}</li>
                      <li>• {language === "en" ? "Verify all calculations" : "Alle Berechnungen prüfen"}</li>
                      <li>• {language === "en" ? "Consult tax professionals" : "Steuerberater konsultieren"}</li>
                      <li>• {language === "en" ? "Review before filing" : "Vor Abgabe prüfen"}</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {helpActiveSection === "tutorial" && (
              <div className="space-y-6">
                <div className="space-y-5 pl-2 pr-1">
                  <div className="flex items-start gap-4">
                    <div className="mt-0.5 bg-blue-600 text-white rounded-full w-9 h-9 flex items-center justify-center flex-shrink-0 font-bold leading-none">1</div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">
                        {language === "en" ? "Select plan & analyze wallet" : "Plan auswählen & Wallet analysieren"}
                      </h4>
                      <p className="text-gray-700">
                        {language === "en"
                          ? "Select a plan first, then choose tax year and country of residence. Enter your first VeChain wallet address and start the analysis. Related VeDelegate wallets are detected automatically via VeDelegate NFT."
                          : "Wählen Sie zuerst einen Plan, anschließend Steuerjahr und Wohnsitzland. Erfassen Sie Ihre erste VeChain-Wallet-Adresse und starten Sie die Analyse – zugehörige VeDelegate-Wallets werden über den jeweiligen VeDelegate-NFT automatisch erkannt."}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="mt-0.5 bg-blue-600 text-white rounded-full w-9 h-9 flex items-center justify-center flex-shrink-0 font-bold leading-none">2</div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">
                        {language === "en" ? "Complete your wallet set" : "Wallet-Set vervollständigen"}
                      </h4>
                      <p className="text-gray-700">
                        {language === "en"
                          ? "Add all of your additional wallets to the wallet set until all relevant addresses are included."
                          : "Fügen Sie alle weiteren eigenen Wallets zum Wallet-Set hinzu, bis alle relevanten Adressen enthalten sind."}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="mt-0.5 bg-blue-600 text-white rounded-full w-9 h-9 flex items-center justify-center flex-shrink-0 font-bold leading-none">3</div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">
                        {language === "en" ? "Confirm plan & complete purchase (if required)" : "Plan bestätigen & ggf. buchen"}
                      </h4>
                      <p className="text-gray-700">
                        {language === "en"
                          ? "If you need more transactions/features, adjust your plan, confirm the GPS prompt, and complete the payment via the VeWorld browser extension."
                          : "Passen Sie bei Bedarf den Plan an, bestätigen Sie die GPS-Abfrage und schließen Sie die Zahlung über die VeWorld Browser-Extension ab."}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="mt-0.5 bg-blue-600 text-white rounded-full w-9 h-9 flex items-center justify-center flex-shrink-0 font-bold leading-none">4</div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">
                        {language === "en" ? "Import exchange CSVs" : "Börsen-CSVs importieren"}
                      </h4>
                      <p className="text-gray-700">
                        {language === "en"
                          ? 'In the "My Wallets" area, upload the CSV files of the exchanges you used. Only with complete exchange data can token swaps and NFT buys/sells be calculated seamlessly using FIFO.'
                          : 'Laden Sie im Bereich „Meine Wallets" die CSV-Dateien der genutzten Börsen hoch. Nur mit vollständigen Börsendaten können Token-Swaps sowie NFT-Käufe und -Verkäufe lückenlos nach FIFO berechnet werden.'}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="mt-0.5 bg-blue-600 text-white rounded-full w-9 h-9 flex items-center justify-center flex-shrink-0 font-bold leading-none">5</div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">
                        {language === "en" ? "Calculate set & review results" : "Set berechnen & Ergebnisse prüfen"}
                      </h4>
                      <p className="text-gray-700">
                        {language === "en"
                          ? 'Start the full tax analysis with "Calculate set". Review the results; if swaps/NFT trades are missing, add the corresponding exchange CSVs.'
                          : 'Starten Sie anschließend mit „Set berechnen" die vollständige Steueranalyse. Prüfen Sie die Ergebnisse – falls Swaps/NFT-Trades fehlen, ergänzen Sie die passenden Börsen-CSVs.'}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="mt-0.5 bg-blue-600 text-white rounded-full w-9 h-9 flex items-center justify-center flex-shrink-0 font-bold leading-none">6</div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">
                        {language === "en" ? "Export your tax reports" : "Steuerberichte exportieren"}
                      </h4>
                      <p className="text-gray-700">
                        {language === "en"
                          ? 'Open "Generate Tax Reports" in the menu to download PDF reports and/or CSV exports for your tax preparation.'
                          : 'Klicken Sie im Menü auf „Steuerberichte erstellen", um PDF-Berichte und/oder CSV-Exporte für Ihre Steuervorbereitung herunterzuladen.'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {helpActiveSection === "faq" && (
              <div className="space-y-4">
                <div className="border border-gray-200 rounded-lg">
                  <details className="group">
                    <summary className="flex justify-between items-center p-4 cursor-pointer hover:bg-gray-50">
                      <span className="font-medium text-gray-900">
                        {language === "en" ? "How accurate are the tax calculations?" : "Wie genau sind die Steuerberechnungen?"}
                      </span>
                      <span className="transform group-open:rotate-180 transition-transform">▼</span>
                    </summary>
                    <div className="px-4 pb-4 text-gray-700 text-sm">
                      {language === "en"
                        ? "V3Tax uses standard FIFO methodology and current market prices. However, tax laws vary by jurisdiction. Always verify calculations with a qualified tax professional."
                        : "V3Tax verwendet standardisierte FIFO-Methodik und aktuelle Marktpreise. Steuergesetze variieren jedoch je nach Rechtsprechung. Lassen Sie Berechnungen immer von einem qualifizierten Steuerberater überprüfen."}
                    </div>
                  </details>
                </div>

                <div className="border border-gray-200 rounded-lg">
                  <details className="group">
                    <summary className="flex justify-between items-center p-4 cursor-pointer hover:bg-gray-50">
                      <span className="font-medium text-gray-900">
                        {language === "en" ? "What file formats can I export?" : "Welche Dateiformate kann ich exportieren?"}
                      </span>
                      <span className="transform group-open:rotate-180 transition-transform">▼</span>
                    </summary>
                    <div className="px-4 pb-4 text-gray-700 text-sm">
                      {language === "en"
                        ? "You can export a comprehensive PDF report or individual CSV files for VET/VTHO transactions, VIP-180 tokens, and NFTs. Choose the format that works best with your tax software."
                        : "Sie können einen umfassenden PDF-Bericht oder einzelne CSV-Dateien für VET/VTHO-Transaktionen, VIP-180-Token und NFTs exportieren. Wählen Sie das Format, das am besten mit Ihrer Steuersoftware funktioniert."}
                    </div>
                  </details>
                </div>

                <div className="border border-gray-200 rounded-lg">
                  <details className="group">
                    <summary className="flex justify-between items-center p-4 cursor-pointer hover:bg-gray-50">
                      <span className="font-medium text-gray-900">
                        {language === "en" ? "Is my wallet data secure?" : "Sind meine Wallet-Daten sicher?"}
                      </span>
                      <span className="transform group-open:rotate-180 transition-transform">▼</span>
                    </summary>
                    <div className="px-4 pb-4 text-gray-700 text-sm">
                      {language === "en"
                        ? "We only access public blockchain data using your wallet addresses. No private keys are ever requested or stored. All processing happens locally in your browser when possible."
                        : "Wir greifen nur auf öffentliche Blockchain-Daten mit Ihren Wallet-Adressen zu. Private Schlüssel werden nie angefordert oder gespeichert. Alle Verarbeitung erfolgt nach Möglichkeit lokal in Ihrem Browser."}
                    </div>
                  </details>
                </div>
              </div>
            )}

            {helpActiveSection === "support" && (
              <div className="space-y-6">
                <div className="rounded-2xl border border-blue-200 bg-blue-50/60 p-5 shadow-sm">
                  <div className="text-sm font-semibold text-gray-900">
                    {language === "en" ? "Need Personal Help?" : "Persönliche Hilfe benötigt?"}
                  </div>
                  <div className="mt-1 text-sm text-gray-700 leading-relaxed">
                    {language === "en"
                      ? "Fill out the form below and our support team will get back to you within 48 hours."
                      : "Füllen Sie das untenstehende Formular aus und unser Support-Team meldet sich innerhalb von 48 Stunden bei Ihnen."}
                  </div>
                </div>

                <div className="rounded-2xl border border-blue-200 bg-blue-50/60 p-5 shadow-sm">
                  <h5 className="text-sm font-semibold text-blue-900">
                    {language === "en" ? "Professional Support Service" : "Professioneller Support-Service"}
                  </h5>
                  <div className="text-blue-800 text-sm leading-relaxed space-y-3 mt-3">
                    <p>
                      {language === "en"
                        ? "Experiencing calculation issues or incorrect transaction classifications? Our support team can help ensure your wallet is analyzed correctly."
                        : "Probleme mit Berechnungen oder falschen Transaktions-Klassifizierungen? Unser Support-Team kann dabei helfen, dass Ihre Wallet korrekt analysiert wird."}
                    </p>

                    <div className="bg-white/60 rounded-lg p-4 border border-blue-300">
                      <p className="font-medium text-blue-900 mb-2">
                        {language === "en" ? "How our manual correction service works:" : "So funktioniert unser manueller Korrektur-Service:"}
                      </p>
                      <ul className="space-y-1 text-blue-700 text-xs">
                        <li>• {language === "en" ? "Create a Support Ticket describing the specific issue" : "Erstellen Sie ein Support-Ticket mit einer Beschreibung des spezifischen Problems"}</li>
                        <li>• {language === "en" ? "If comfortable, provide your wallet address in the ticket" : "Falls gewünscht, geben Sie Ihre Wallet-Adresse im Ticket an"}</li>
                        <li>• {language === "en" ? "Include consent for V3Tax to analyze your public blockchain data" : "Geben Sie Ihr Einverständnis, dass V3Tax Ihre öffentlichen Blockchain-Daten analysiert"}</li>
                        <li>• {language === "en" ? "Our team manually reviews and corrects calculation errors" : "Unser Team überprüft und korrigiert Berechnungsfehler manuell"}</li>
                        <li>• {language === "en" ? "You receive an updated, accurate tax report" : "Sie erhalten einen aktualisierten, korrekten Steuerbericht"}</li>
                      </ul>
                    </div>

                    <p className="text-xs text-blue-600">
                      {language === "en"
                        ? "🔒 We only access publicly available blockchain data"
                        : "🔒 Wir greifen nur auf öffentlich verfügbare Blockchain-Daten zu"}
                    </p>
                  </div>
                </div>

                {supportSubmitted ? (
                  <div className="text-center py-8">
                    <div className="text-6xl mb-4">✅</div>
                    <h4 className="text-xl font-semibold text-green-800 mb-2">
                      {language === "en" ? "Support Ticket Submitted!" : "Support-Ticket gesendet!"}
                    </h4>
                    <p className="text-green-700 mb-4">
                      {language === "en"
                        ? "Your support request has been sent to our team. We will get back to you within 48 hours."
                        : "Ihre Support-Anfrage wurde an unser Team gesendet. Wir melden uns innerhalb von 48 Stunden bei Ihnen."}
                    </p>
                    <button
                      onClick={() => {
                        setSupportSubmitted(false);
                        setSupportForm({
                          email: "",
                          category: "",
                          description: "",
                          urgent: false,
                          walletAddress: "",
                          consent: false,
                          calculationErrorConsent: false,
                        });
                        setSupportAttachments([]);
                      }}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
                      type="button"
                    >
                      {language === "en" ? "Submit Another Ticket" : "Weiteres Ticket senden"}
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSupportSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {language === "en" ? "Email Address" : "E-Mail Adresse"} <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        required
                        value={supportForm.email || ""}
                        onChange={(e) => setSupportForm({ ...supportForm, email: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder={language === "en" ? "john.doe@email.com" : "Max.Mustermann@email.com"}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {language === "en" ? "Issue Category" : "Problem-Kategorie"} <span className="text-red-500">*</span>
                      </label>
                      <select
                        required
                        value={supportForm.category || ""}
                        onChange={(e) => setSupportForm({ ...supportForm, category: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">{language === "en" ? "Select category..." : "Kategorie wählen..."}</option>
                        <option value={language === "en" ? "Technical Problem" : "Technisches Problem"}>
                          {language === "en" ? "Technical Problem" : "Technisches Problem"}
                        </option>
                        <option value={language === "en" ? "Calculation Error" : "Berechnungsfehler"}>
                          {language === "en" ? "Calculation Error" : "Berechnungsfehler"}
                        </option>
                        <option value={language === "en" ? "Export Issues" : "Export-Probleme"}>
                          {language === "en" ? "Export Issues" : "Export-Probleme"}
                        </option>
                        <option value={language === "en" ? "CSV Import Issues" : "CSV-Import Probleme"}>
                          {language === "en" ? "CSV Import Issues" : "CSV-Import Probleme"}
                        </option>
                        <option value={language === "en" ? "General Question" : "Allgemeine Frage"}>
                          {language === "en" ? "General Question" : "Allgemeine Frage"}
                        </option>
                        <option value={language === "en" ? "Feature Request" : "Feature-Anfrage"}>
                          {language === "en" ? "Feature Request" : "Feature-Anfrage"}
                        </option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {language === "en" ? "Wallet Addresses (Optional)" : "Wallet-Adressen (Optional)"}
                      </label>
                      <textarea
                        value={supportForm.walletAddress || ""}
                        onChange={(e) => setSupportForm({ ...supportForm, walletAddress: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[100px] resize-y font-mono text-sm"
                        placeholder={
                          language === "en"
                            ? "0x1234...\n0xabcd...\n(optional, one wallet address per line)"
                            : "0x1234...\n0xabcd...\n(optional, eine Wallet-Adresse pro Zeile)"
                        }
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {language === "en" ? "Description" : "Beschreibung"} <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        required
                        value={supportForm.description || ""}
                        onChange={(e) => setSupportForm({ ...supportForm, description: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[140px] resize-y"
                        placeholder={
                          language === "en"
                            ? "Please describe the issue in as much detail as possible..."
                            : "Bitte beschreiben Sie das Problem so detailliert wie möglich..."
                        }
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {language === "en"
                          ? "Attachments (Screenshots, CSV files, etc.)"
                          : "Anhänge (Screenshots, CSV-Dateien, etc.)"}{" "}
                        <span className="text-gray-500 text-xs">
                          ({language === "en" ? "Optional" : "Optional"})
                        </span>
                      </label>

                      <div
                        className={`relative border-2 border-dashed rounded-xl p-6 transition-all duration-200 ${
                          isDragOver
                            ? "border-blue-500 bg-blue-50 scale-[1.02]"
                            : "border-gray-300 bg-gray-50 hover:border-blue-400 hover:bg-blue-50/50"
                        }`}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsDragOver(true);
                        }}
                        onDragLeave={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsDragOver(false);
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsDragOver(false);

                          const files = Array.from(e.dataTransfer.files || []).filter((file) => {
                            const ext = file.name.split(".").pop()?.toLowerCase();
                            return ["jpg", "jpeg", "png", "gif", "pdf", "csv", "txt", "json"].includes(ext || "");
                          });

                          if (files.length > 0) {
                            setSupportAttachments((prev) => [...prev, ...files]);
                          }
                        }}
                      >
                        <input
                          type="file"
                          id="file-upload-help-support"
                          multiple
                          accept="image/*,.csv,.txt,.pdf,.json"
                          onChange={(e) => {
                            const files = Array.from(e.target.files || []);
                            setSupportAttachments((prev) => [...prev, ...files]);
                            e.target.value = "";
                          }}
                          className="hidden"
                        />

                        <label
                          htmlFor="file-upload-help-support"
                          className="flex flex-col items-center justify-center cursor-pointer"
                        >
                          <div className="mb-3">
                            <svg
                              className={`w-12 h-12 transition-colors ${
                                isDragOver ? "text-blue-500" : "text-gray-400"
                              }`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                              />
                            </svg>
                          </div>

                          <div className="text-center">
                            <span
                              className={`text-sm font-medium transition-colors ${
                                isDragOver ? "text-blue-600" : "text-gray-700"
                              }`}
                            >
                              {isDragOver
                                ? language === "en"
                                  ? "Drop files here"
                                  : "Dateien hier ablegen"
                                : language === "en"
                                ? "Click to upload or drag and drop"
                                : "Klicken zum Hochladen oder Drag & Drop"}
                            </span>
                            <p className="text-xs text-gray-500 mt-1">
                              {language === "en"
                                ? "PNG, JPG, PDF, CSV, TXT, JSON (max. 10 MB per file)"
                                : "PNG, JPG, PDF, CSV, TXT, JSON (max. 10 MB pro Datei)"}
                            </p>
                          </div>

                          <button
                            type="button"
                            className={`mt-3 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                              isDragOver
                                ? "bg-blue-600 text-white shadow-lg"
                                : "bg-blue-500 text-white hover:bg-blue-600 shadow-md hover:shadow-lg"
                            }`}
                            onClick={(e) => {
                              e.preventDefault();
                              document.getElementById("file-upload-help-support")?.click();
                            }}
                          >
                            {language === "en" ? "Select Files" : "Dateien auswählen"}
                          </button>
                        </label>
                      </div>

                      {supportAttachments.length > 0 && (
                        <div className="mt-4 space-y-2">
                          <div className="text-xs font-medium text-gray-600 mb-2">
                            {language === "en"
                              ? `${supportAttachments.length} file(s) selected`
                              : `${supportAttachments.length} Datei(en) ausgewählt`}
                          </div>

                          {supportAttachments.map((file, index) => {
                            const fileIcon = file.type?.startsWith("image/")
                              ? "🖼️"
                              : file.name.endsWith(".csv")
                              ? "📊"
                              : file.name.endsWith(".pdf")
                              ? "📄"
                              : file.name.endsWith(".json")
                              ? "📋"
                              : "📎";

                            return (
                              <div
                                key={index}
                                className="flex items-center justify-between bg-white border border-gray-200 rounded-lg px-4 py-3 shadow-sm hover:shadow-md transition-shadow"
                              >
                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                  <span className="text-2xl flex-shrink-0">{fileIcon}</span>
                                  <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium text-gray-900 truncate">
                                      {file.name}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      {(file.size / 1024).toFixed(1)} KB
                                    </div>
                                  </div>
                                </div>

                                <button
                                  type="button"
                                  onClick={() => {
                                    const newFiles = [...supportAttachments];
                                    newFiles.splice(index, 1);
                                    setSupportAttachments(newFiles);
                                  }}
                                  className="ml-3 p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                                  title={language === "en" ? "Remove file" : "Datei entfernen"}
                                >
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    <div className="space-y-3">
                      <label className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          required
                          checked={supportForm.consent}
                          onChange={(e) => setSupportForm({ ...supportForm, consent: e.target.checked })}
                          className="rounded border-gray-300 mt-1"
                        />
                        <span className="text-sm text-gray-700">
                          {language === "en"
                            ? "I consent to the processing of my submitted data for support purposes."
                            : "Ich stimme der Verarbeitung meiner übermittelten Daten zu Support-Zwecken zu."}
                        </span>
                      </label>

                      <label className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={supportForm.urgent}
                          onChange={(e) => setSupportForm({ ...supportForm, urgent: e.target.checked })}
                          className="rounded border-gray-300 mt-1"
                        />
                        <span className="text-sm text-gray-700">
                          {language === "en"
                            ? "Mark as urgent"
                            : "Als dringend markieren"}
                        </span>
                      </label>

                      {(supportForm.category === "Calculation Error" ||
                        supportForm.category === "Berechnungsfehler") && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                          <div className="flex items-start gap-2">
                            <input
                              type="checkbox"
                              id="calculationErrorConsent"
                              checked={supportForm.calculationErrorConsent}
                              onChange={(e) =>
                                setSupportForm({
                                  ...supportForm,
                                  calculationErrorConsent: e.target.checked,
                                })
                              }
                              className="rounded border-gray-300 mt-1"
                            />
                            <label htmlFor="calculationErrorConsent" className="text-sm text-gray-700">
                              <span className="font-medium text-yellow-800">
                                {language === "en"
                                  ? "🔍 Detailed Calculation Error Analysis:"
                                  : "🔍 Detaillierte Berechnungsfehler-Analyse:"}
                              </span>
                              <br />
                              {language === "en"
                                ? "I additionally consent to V3Tax performing a detailed analysis of my wallet data to identify and resolve calculation errors."
                                : "Ich erkläre mich zusätzlich einverstanden, dass V3Tax eine detaillierte Analyse meiner Wallet-Daten durchführt, um Berechnungsfehler zu identifizieren und zu beheben."}
                            </label>
                          </div>
                        </div>
                      )}
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmittingSupport}
                      className="w-full calculate-button disabled:bg-gray-400 text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2 animate-fast-pulse"
                    >
                      {isSubmittingSupport ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          {language === "en" ? "Sending..." : "Wird gesendet..."}
                        </>
                      ) : (
                        <>
                          <span className="text-lg">📨</span>
                          {language === "en" ? "Submit Support Ticket" : "Support-Ticket senden"}
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            )}
          </div>

          <div
            className={`shrink-0 border-t border-gray-200 bg-white ${
              isHandheldDevice ? "px-4 py-3" : "flex justify-end px-6 py-4"
            }`}
          >
            <div className={isHandheldDevice ? "flex justify-end" : ""}>
              <button
                type="button"
                onClick={closeModal}
                className="rounded-lg bg-blue-600 px-6 py-2 font-medium text-white transition hover:bg-blue-700"
              >
                {language === "en" ? "Close" : "Schließen"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </ModalMotionShell>
  );
}
